import type { Frame, Icon } from "@judo/model-api";
import { Card, CardContent, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import type { ReactNode } from "react";
import { IconRenderer } from "./IconRenderer";

export interface FrameRendererProps {
	frame: Frame;
	children: ReactNode;
	/** Optional label for the card header */
	label?: string;
	/** Optional icon for the card header */
	icon?: Icon;
}

/**
 * Renderer for Frame (card/panel) styling.
 * Renders a Card with CardContent and optional header containing icon and label.
 * Matches the template's Card/CardContent pattern with Grid-based header layout.
 */
export function FrameRenderer({ frame, children, label, icon }: FrameRendererProps) {
	const hasHeader = label || icon;

	return (
		<Card data-testid={`frame::${frame["xmi:id"]}`}>
			<CardContent>
				{hasHeader && (
					<Grid
						container
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 2 }}
						data-testid={`frame::${frame["xmi:id"]}::header`}
					>
						<Grid>
							<Grid container direction="row" alignItems="center" justifyContent="flex-start">
								{icon && <IconRenderer icon={icon} sx={{ marginRight: 1 }} />}
								{label && (
									<Typography variant="h5" component="h1">
										{label}
									</Typography>
								)}
							</Grid>
						</Grid>
					</Grid>
				)}
				{children}
			</CardContent>
		</Card>
	);
}
